// Admin / Production: Njord threshold alerts via the shared desktop notification stack.
// In-app toast when the tab is visible; OS notification when the tab is in the background.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import {
    userHasAdminOrProductionRole,
    showAdminDesktopNotificationIfAllowed,
} from '../services/adminDesktopNotificationService';
import { subscribeNjordLiveHub, syncNjordLiveSensorIds, NJORD_LIVE_POLL_INTERVAL_MS } from '../services/njord/njordLiveHub';
import { buildAlertFeed, buildMonitorSensorList } from '../services/njord/njordMonitorUtils';
import { isNjordApiReady } from '../services/njord/njordReadiness';
import { fetchNjordRooms } from '../services/njord/njordRoomService';
import { fetchNjordSensors } from '../services/njord/njordRegistryService';
import {
    findNewNjordSensorAlerts,
    formatNjordSensorAlertNotification,
    njordAlertKeysSet,
    njordAlertNotificationKey,
    njordSensorAlertNavigateHref,
} from '../services/njord/njordSensorDesktopNotificationUtils';
import ToastNotification from './ToastNotification';
import './ToastNotification.css';

const NOTIFY_COOLDOWN_MS = 2 * 60 * 1000;
const MAX_TOASTS = 3;

/**
 * @param {Record<string, unknown>} userData
 * @param {string} title
 * @param {string} body
 * @param {{ tag: string, onClickNavigate: string }} options
 */
async function notifyWithFreshRoleCheck(userData, title, body, options) {
    const uid = userData?.uid;
    if (!uid) return false;
    try {
        const snap = await getDoc(doc(db, 'users', uid));
        const fresh = snap.exists() ? { uid, ...snap.data() } : { uid, role: 'Customer' };
        if (!userHasAdminOrProductionRole(fresh)) return false;
        return showAdminDesktopNotificationIfAllowed(fresh, title, body, options);
    } catch (e) {
        console.warn('[NjordSensorDesktopNotifier] role re-check', e);
        return false;
    }
}

export default function NjordSensorDesktopNotifier() {
    const { user } = useAuth();
    const [userData, setUserData] = useState(null);
    const [live, setLive] = useState({});
    const [rooms, setRooms] = useState([]);
    const [registry, setRegistry] = useState([]);
    const [toasts, setToasts] = useState([]);
    const prevAlertKeysRef = useRef(null);
    const lastNotifiedAtRef = useRef(new Map());
    const apiReady = isNjordApiReady();

    useEffect(() => {
        if (!user?.uid) {
            setUserData(null);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'users', user.uid));
                if (cancelled) return;
                const data = snap.exists()
                    ? { uid: user.uid, ...snap.data() }
                    : { uid: user.uid, role: 'Customer' };
                setUserData(data);
            } catch (e) {
                console.error('[NjordSensorDesktopNotifier] user load', e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [user]);

    useEffect(() => {
        if (!apiReady || !userData || !userHasAdminOrProductionRole(userData)) return undefined;
        return subscribeNjordLiveHub(setLive);
    }, [apiReady, userData]);

    useEffect(() => {
        if (!apiReady || !userData || !userHasAdminOrProductionRole(userData)) return undefined;
        let cancelled = false;

        const loadRegistry = async () => {
            try {
                const [r, reg] = await Promise.all([fetchNjordRooms(), fetchNjordSensors()]);
                if (cancelled) return;
                setRooms(r || []);
                setRegistry(reg || []);
                syncNjordLiveSensorIds(
                    (reg || []).map((s) => s.id || s.sensorId).filter(Boolean)
                );
            } catch (e) {
                console.warn('[NjordSensorDesktopNotifier] registry load', e);
            }
        };

        loadRegistry();
        const timer = setInterval(loadRegistry, NJORD_LIVE_POLL_INTERVAL_MS);
        const onVisible = () => {
            if (typeof document !== 'undefined' && !document.hidden) loadRegistry();
        };
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', onVisible);
        }
        return () => {
            cancelled = true;
            clearInterval(timer);
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', onVisible);
            }
        };
    }, [apiReady, userData]);

    const sensors = useMemo(
        () => buildMonitorSensorList(registry, live, rooms, { connectedOnly: false }),
        [registry, live, rooms]
    );

    const alerts = useMemo(() => buildAlertFeed(sensors), [sensors]);

    useEffect(() => {
        if (!userData || !userHasAdminOrProductionRole(userData) || !apiReady) return;

        const currentKeys = njordAlertKeysSet(alerts);

        if (prevAlertKeysRef.current === null) {
            prevAlertKeysRef.current = currentKeys;
            return;
        }

        const freshAlerts = findNewNjordSensorAlerts(prevAlertKeysRef.current, alerts);
        prevAlertKeysRef.current = currentKeys;

        if (!freshAlerts.length) return;

        const now = Date.now();
        for (const alert of freshAlerts) {
            const key = njordAlertNotificationKey(alert);
            const lastAt = lastNotifiedAtRef.current.get(key);
            if (lastAt != null && now - lastAt < NOTIFY_COOLDOWN_MS) continue;
            lastNotifiedAtRef.current.set(key, now);

            const { title, body } = formatNjordSensorAlertNotification(alert);
            const tag = `lsc-njord-alert-${key}`;
            const onClickNavigate = njordSensorAlertNavigateHref(alert.sensorId);

            if (typeof document !== 'undefined' && document.hidden) {
                void notifyWithFreshRoleCheck(userData, title, body, { tag, onClickNavigate });
            } else {
                setToasts((prev) => {
                    const toast = {
                        id: tag,
                        title,
                        message: body,
                        type: alert.severity === 'critical' ? 'error' : 'warning',
                        duration: 8000,
                        position: 'top-right',
                        ctaText: 'Open sensor',
                        ctaLink: '/odin',
                        njordDeepLink: onClickNavigate.replace(/^\/odin#/, ''),
                    };
                    const updated = [...prev, toast];
                    return updated.length > MAX_TOASTS ? updated.slice(-MAX_TOASTS) : updated;
                });
            }
        }
    }, [alerts, userData, apiReady]);

    const handleDismiss = useCallback((toastId) => {
        setToasts((prev) => prev.filter((t) => t.id !== toastId));
    }, []);

    const handleCTAClick = useCallback((_toastId, _ctaLink, toast) => {
        if (toast?.njordDeepLink) {
            window.location.assign(`/odin#${toast.njordDeepLink}`);
        }
    }, []);

    if (!userData || !userHasAdminOrProductionRole(userData)) {
        return null;
    }

    return (
        <div className="toast-container toast-top-right">
            {toasts.map((toast) => (
                <ToastNotification
                    key={toast.id}
                    notification={toast}
                    onDismiss={handleDismiss}
                    onCTAClick={(id, link) => handleCTAClick(id, link, toast)}
                    position="top-right"
                />
            ))}
        </div>
    );
}
